import { supabase } from '../supabaseClient';

// 5-minute in-memory cache: cacheKey -> { rows, ts }
const cache = new Map();
const CACHE_TTL = 5 * 60 * 1000;

function normalizeKey(title, author) {
  return `${(title || '').toLowerCase().trim()}|${(author || '').toLowerCase().trim()}`;
}

function applyRatings(books, rows) {
  // Group ratings by normalized title+author
  const stats = {};
  rows.forEach(r => {
    const key = normalizeKey(r.book_title, r.book_author);
    if (!stats[key]) stats[key] = [];
    stats[key].push(r.rating);
  });

  return books.map(book => {
    const key = normalizeKey(book.title, book.author);
    const ratings = stats[key];
    if (ratings && ratings.length >= 3) {
      // 3+ community ratings — switch to community rating
      const avg = ratings.reduce((sum, r) => sum + r, 0) / ratings.length;
      return {
        ...book,
        rating: Math.round(avg * 10) / 10,
        ratingSource: 'community',
        ratingCount: ratings.length,
      };
    }
    if (ratings && ratings.length > 0) {
      // 1–2 community ratings — keep Google rating but record community count
      return {
        ...book,
        ratingSource: book.ratingSource || 'google',
        ratingCount: ratings.length,
      };
    }
    return {
      ...book,
      ratingSource: book.ratingSource || 'google',
    };
  });
}

/**
 * Takes an array of book objects and returns a new array with community ratings
 * hydrated from Supabase. Books matched by title + author (case-insensitive).
 * Falls back to existing book.rating / 'google' source when no community ratings exist.
 * Results are cached for 5 minutes.
 */
export async function hydrateWithCommunityRatings(books) {
  if (!books || books.length === 0) return books;

  const titles = [...new Set(books.map(b => b.title).filter(Boolean))];

  const cacheKey = titles.slice().sort().join('\x00');
  const cached = cache.get(cacheKey);
  if (cached && Date.now() - cached.ts < CACHE_TTL) {
    return applyRatings(books, cached.rows);
  }

  try {
    const { data: rows } = await supabase
      .from('reviews')
      .select('book_title, book_author, rating')
      .in('book_title', titles)
      .not('rating', 'is', null);

    const resolved = rows || [];
    cache.set(cacheKey, { rows: resolved, ts: Date.now() });
    return applyRatings(books, resolved);
  } catch {
    // On error return books with ratingSource set but ratings unchanged
    return books.map(b => ({ ...b, ratingSource: b.ratingSource || 'google' }));
  }
}
